import {
    SimilarityColor,
    SIMILARITY_CONFIG_TEXT,
} from '@patsnap-utils/sequence';
import { get } from 'lodash';
import { invalidWordReg } from './constants';

interface ISegment {
    start: number;
    end: number;
    highlightNos: number[];
}

interface IHighlight {
    start: number;
    end: number;
    color?: string;
}

interface IHit {
    subjectStart: number;
    subjectEnd: number;
    similarityValue?: number;
}

const BLANK = '\u00A0';
const similarityInstance = new SimilarityColor(SIMILARITY_CONFIG_TEXT);

export const transferHitToHighlight = (hits: IHit[]): IHighlight[] => hits.map((hit) => {
    let { subjectStart = 0, subjectEnd = 0 } = hit;
    // 检查是否反向
    if (subjectStart > subjectEnd) {
        const temp = subjectStart;
        subjectStart = subjectEnd;
        subjectEnd = temp;
    }

    return {
        start: subjectStart,
        end: subjectEnd,
        color: get(
            hit.similarityValue
                ? similarityInstance.similarity(hit.similarityValue)
                : {},
            'color',
        ),
    };
});

/**
* 拆分长序列多段 方便用于多行展示
*/
export const splitIntoSegments = (sequence: string, length: number) => {
   const segments = [];
   let preEnd = 0;
   let startIndex = 0;
   let endIndex = 0;
   for (let i = 0, size = sequence.length; i < size;) {
       const end = i + length;
       const segment = sequence.slice(i, end);
       // 计算有效序列字符的数量
       const validLength = segment.replace(invalidWordReg, '').length;
       if (validLength > 0) {
           startIndex = preEnd + 1;
           endIndex = startIndex + validLength - 1;
       } else {
           startIndex = preEnd;
           endIndex = preEnd;
       }
       segments.push({
           start: startIndex, // 有效序列的起始位置
           segment,
           end: endIndex, // 有效序列的结束位置
       });
       preEnd = endIndex;
       i = end;
   }
   return segments;
};

/**
 * 计算select的方向
 * @param startTop
 * @param startLeft
 * @param endTop
 * @param endLeft
 * @returns
 */
export const calSelectDirection = (startTop: number, startLeft: number, endTop: number, endLeft: number) => {
    // 计算选择方向 1-正选 0-反选
    if (Math.abs(endTop - startTop) > 16) {
        // 行高
        if (endTop > startTop) {
            return 1;
        }
        return 0;
    }
    if (endLeft > startLeft) {
        return 1;
    }
    return 0;
}

/**
 * 计算划选的起始字符位置 排除空格
 * @param selection
 * @param dir
 * @returns
 */
export const calStartWord = (selection: Selection, dir: number) => {
    let { anchorOffset } = selection;
    // 以空格开头的 +1 就当没有划到空格
    if (selection.toString().substr(0, 1) === ' ') {
        anchorOffset += 1;
    }
    let spaceNum;
    let isRight;
    let anchorOffsetExcludeSpace;
    do {
        spaceNum = spaceNum
            ? spaceNum - 1
            : parseInt(((anchorOffset - 1) / 10).toString(), 10);
        anchorOffsetExcludeSpace = anchorOffset - spaceNum;
        isRight = dir
            ? spaceNum === parseInt((anchorOffsetExcludeSpace / 10).toString(), 10) // 正选 70-79 是7个
            : spaceNum
              === parseInt(((anchorOffsetExcludeSpace - 1) / 10).toString(), 10); // 反选的时候 71-80 是7个
    } while (!isRight && spaceNum > 0);
    return anchorOffsetExcludeSpace;
}

function between(min: number, max: number, val: number) {
    return val >= min && val <= max;
}

function one2Three(start: number, end: number, splitStart: number, splitEnd: number, highlightNos: number[], index: number ): ISegment[] {
    return [
        { start: start, end: splitStart - 1, highlightNos: highlightNos.slice()},
        { start: splitStart, end: splitEnd, highlightNos: highlightNos.slice().concat([index])},
        { start: splitEnd + 1 , end: end, highlightNos: highlightNos.slice()},
    ]
}

function one2Two(start: number, end: number, splitIndex: number, highlightNos: number[]): ISegment[] {
    return [
        { start: start, end: splitIndex - 1, highlightNos: highlightNos.slice()},
        { start: splitIndex, end: end, highlightNos: highlightNos.slice()},
    ]
}

// 计算高亮片段
export function calculate(ctx: any, sequence: string, lights: IHighlight[], segmentLength: number, degenerateNos: number[]) {
    let segments: SubSequence[] = [];
    // 遍历高亮数组，计算出有重叠的高亮
    let segmentsAfterLight: ISegment[] = [];
    const validLights = lights.filter(x => x.start <= sequence.length);
    if (!validLights.length){
        const subSequence = new SubSequence(ctx, sequence, 0, sequence.length - 1, [], [], segmentLength, degenerateNos);
        return [subSequence];
    }
    validLights.forEach((x, index) => {
        if (segmentsAfterLight.length === 0) {
            const temArr = one2Three(1, sequence.length, x.start, x.end, [], index);
            segmentsAfterLight.push(...temArr);
        } else {
            let tempArr = segmentsAfterLight.map(y => {
                // 整段都命中
                if (x.start <= y.start && x.end >= y.end) {
                    y.highlightNos.push(index)
                    return y;
                } else if (x.start > y.end || x.end < y.start) {
                    // 整段都不命中
                    return y;
                } else if (between(y.start, y.end, x.start) && between(y.start, y.end, x.end)) {
                    // 有交叉的三种情况
                    const tempArr1 = one2Three(y.start, y.end, x.start, x.end, y.highlightNos, index)
                    return tempArr1;
                } else if (between(y.start, y.end, x.start)) {
                    const tempArr1 = one2Two(y.start, y.end, x.start, y.highlightNos)
                    tempArr1[1].highlightNos.push(index);
                    return tempArr1;
                } else if (between(y.start, y.end, x.end)) {
                    const tempArr1 = one2Two(y.start, y.end, x.end + 1, y.highlightNos)
                    tempArr1[0].highlightNos.push(index);
                    return tempArr1;
                }
                return []
            })
            segmentsAfterLight = [];
            tempArr.forEach(f => {
                Array.isArray(f)
                    ? segmentsAfterLight.push(...f)
                    : segmentsAfterLight.push(f);
            });
        }

        segmentsAfterLight = segmentsAfterLight.filter(x => x.end - x.start >= 0)
    })
    segments = segmentsAfterLight.map(x => {
        const end = Math.min(x.end, sequence.length);
        return new SubSequence(ctx, sequence.substring(x.start - 1, end), x.start - 1, end - 1, x.highlightNos, validLights , segmentLength, degenerateNos)
    })
    return segments
}
// 高亮片段重叠，拆分成重叠片段和其他片段
// 索引从0开始？ 感觉从1开始更方便
export class SubSequence {
    sequence: string = '';
    start: number = -1;
    end: number = -1;
    highlightNos: number[] = [];
    highlights: IHighlight[] = [];
    spaces: number[] = [];
    wraps: number[] = [];
    boundaries: number[] = [];
    degenerateNos: number[] = [];
    spaceLength = 10;
    rowLength = 60;
    ctx: any;

    constructor(ctx: any, sequence: string, start: number, end: number, highlightNos: number[], highlights: IHighlight[] , segmentLength: number, degenerateNos: number[] ) {
        this.ctx = ctx;
        this.sequence = sequence;
        this.start = start;
        this.end = end;
        this.highlightNos = highlightNos;
        this.rowLength = segmentLength;
        this.highlights = highlights;
        this.setBoundary();
        this.setDegenerate(degenerateNos);
        this.appendSpace();
        this.appendWrap();
    }

    private createHoverWrapper(kids: any, no: number) {
        const h = this.ctx.$createElement;
        return [h('span', { attrs: { 'data-no': no } }, kids)];
    }

    private createDegenerateWrapper(kids: any) {
        const h = this.ctx.$createElement;
        return [h('span', { attrs: { class: 'degenerate', 'data-highlight-target': 'degenerate' } }, kids)]
    }

    /**
     * degenerate是原有逻辑，保留不做修改
     * 除此之外的高亮皆为可自定义
     * 包裹结构：
     *
     * 1. 没有高亮, raw text
     * 没有degenerate高亮，经过作用域插槽后依然是string
     *
     * T
     *
     * 2. 有高亮
     * data-no：第几位
     * data-highlight-target：标记了是属于那种类型的高亮
     *
     * <span data-no="4">
     *     <span data-highlight-target="degenerate">T</span>
     * </span>
     *
     * 3. 多种高亮，每一种一层span
     *
     * <span data-no="3">
     *     <span data-highlight-target="degenerate">
     *         <span data-highlight-target="modification">T</span>
     *     </span>
     * </span>
     * */
    get formattedSequence(): any[] {
        if (!this.sequence) {
            return [];
        }

        const h = this.ctx.$createElement;

        let tempSeqArr: string[] = this.sequence.split('');

        const customSlotRender = typeof this.ctx.$scopedSlots.label === 'function' ? this.ctx.$scopedSlots.label : undefined


        // 用自定义插槽包裹
        let tempSeq: any[] = tempSeqArr.map((x, i) => {
            const no = i + this.start + 1
            const customRendered = customSlotRender ? customSlotRender({
                index: i + this.start,
                location: no,
                node: x,
                label: this.sequence[i],
            }) : null;

            // 自定义插槽没渲染，忽略
            let rendered = customRendered ? customRendered : x

            // 内置degenerate高亮不变
            if (this.degenerateNos.includes(i + this.start)) {
                rendered = this.createDegenerateWrapper(rendered)
            }

            // 是string，没有经过degenerate和自定义插槽
            // 直接输出string，等待后续合并
            return typeof rendered === 'string' ? rendered : this.createHoverWrapper(rendered, no);
        });

        this.boundaries.forEach(x => {
            const index = x - this.start;
            tempSeq[index] = h('b', { class: `arrow-sign ${ x === this.end ? 'end' : ''  }` }, tempSeq[index]);
        });

        this.spaces.forEach(x => {
            const index = x - this.start;
            const i = tempSeq[index];
            if (i === undefined) return;
            tempSeq[index] = typeof i !== 'string' ? [i, BLANK] : `${i}${BLANK}`;
        })

        this.wraps.forEach(x => {
            const index = x - this.start;
            if (tempSeq[index]) {
                tempSeq[index] = [tempSeq[index], h('br')];
            }
        })

        tempSeq = tempSeq.reduce((prev, curr) => {
            if (typeof curr === 'string' && typeof prev[prev.length - 1] === 'string') {
                prev[prev.length - 1] = prev[prev.length - 1] + curr;
            } else {
                prev.push(curr);
            }
            return prev;
        }, [] as any[]);

        return tempSeq;
    }

    get useHighLight() {
        return this.highlightNos[this.highlightNos.length - 1];
    }

    get useHighlightColor() {
        const curHighlight = this.highlights[this.useHighLight];
        if(this.highlights[this.useHighLight]) {
            return curHighlight.color;
        }
        return '';
    }

    setBoundary() {
        if (this.highlights.find(x => x.start - 1 === this.start || x.end - 1 === this.start)) {
            this.boundaries.push(this.start);
        }
        if (this.highlights.find(x => x.start - 1 === this.end || x.end - 1 === this.end)) {
            this.boundaries.push(this.end);
        }
    }

    setDegenerate(degenerateNos: number[]) {
        this.degenerateNos = degenerateNos.filter(x => x - 1 >= this.start && x - 1 <= this.end).map(x => x - 1);
    }

    // 控制换行
    appendWrap() {
        const remainder = (this.end + 1) % this.rowLength;
        let num = this.end - remainder;
        while(num >= this.start) {
            if (num !== 0){
                this.wraps.push(num);
            }
            num -= this.rowLength;
        }
    }

    // 自己每10个补个空格
    appendSpace() {
        const remainder = (this.end + 1) % this.spaceLength;
        let num = this.end - remainder;
        while(num >= this.start) {
            if (num % this.rowLength !== this.rowLength - 1 && num !== 0) {
                this.spaces.push(num);
            }
            num -= this.spaceLength;
        }
    }
}
